import {
  PortalToolExecutePayloadSchema,
  type PortalToolExecutePayload,
} from '@terra/shared-dto-chat-types';
import { hasActiveChat } from '../../../store/chat-instance-presence';
import type { ExternalToolResultCoordinator } from '../external-tool-batch.types';
import { getExternalToolResultForRetry } from '../external-tool-result-coordinator';
import type { TerminalExecutionQueue } from '../terminal-execution-queue';
import {
  dropSuppressedForegroundExecute,
  handshakeFailureKind,
} from './handshake-gate';
import { portalToolRegistry } from './portal-tool-registry';
import {
  buildPortalExternalToolResult,
  buildPortalToolErrorResult,
  type PortalToolHandlerResult,
} from './types';

type PortalExecuteTracker = {
  registerExecution: (params: {
    toolCallId: string;
    chatId?: string;
  }) => 'execute' | 'duplicate';
};

function buildHandshakeFailureResult({
  payload,
  failureKind,
}: {
  payload: PortalToolExecutePayload;
  failureKind: 'timeout' | 'abort';
}): PortalToolHandlerResult {
  return buildPortalToolErrorResult({
    toolName: payload.toolName,
    toolCallId: payload.toolCallId,
    batchId: payload.batchId,
    message:
      failureKind === 'abort'
        ? 'Portal tool execution aborted'
        : 'Timed out waiting for portal tool batch',
  });
}

async function completeHandshakeFailure({
  coordinator,
  payload,
  failureKind,
}: {
  coordinator: ExternalToolResultCoordinator;
  payload: PortalToolExecutePayload;
  failureKind: 'timeout' | 'abort';
}): Promise<void> {
  coordinator.registerRequest({
    batchId: payload.batchId,
    chatId: payload.sessionId,
    toolCallId: payload.toolCallId,
    toolName: payload.toolName,
  });
  coordinator.clearExecutePending({
    batchId: payload.batchId,
    chatId: payload.sessionId,
    toolCallId: payload.toolCallId,
  });

  await coordinator.completeRequest(
    buildPortalExternalToolResult({
      batchId: payload.batchId,
      chatId: payload.sessionId,
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
      handlerResult: buildHandshakeFailureResult({ payload, failureKind }),
    }),
  );
}

async function reflushDuplicateResult({
  coordinator,
  payload,
}: {
  coordinator: ExternalToolResultCoordinator;
  payload: PortalToolExecutePayload;
}): Promise<void> {
  coordinator.clearExecutePending({
    batchId: payload.batchId,
    chatId: payload.sessionId,
    toolCallId: payload.toolCallId,
  });
  const cachedResult = getExternalToolResultForRetry({
    batchId: payload.batchId,
    chatId: payload.sessionId,
    toolCallId: payload.toolCallId,
  });

  if (!cachedResult) {
    console.warn(
      '[PortalToolProvider] Duplicate toolCallId with no cached result to re-flush',
      {
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        batchId: payload.batchId,
      },
    );

    return;
  }

  try {
    await coordinator.completeRequest(cachedResult);
  } catch (error: unknown) {
    console.warn(
      '[PortalToolProvider] Failed to re-flush duplicate tool result',
      {
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        batchId: payload.batchId,
        error: error instanceof Error ? error : new Error(String(error)),
      },
    );
  }
}

async function executeRegisteredTool({
  coordinator,
  payload,
}: {
  coordinator: ExternalToolResultCoordinator;
  payload: PortalToolExecutePayload;
}): Promise<void> {
  const handler = portalToolRegistry.getHandler(payload.toolName);

  if (!handler) {
    await coordinator.completeRequest(
      buildPortalExternalToolResult({
        batchId: payload.batchId,
        chatId: payload.sessionId,
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        handlerResult: buildPortalToolErrorResult({
          toolName: payload.toolName,
          toolCallId: payload.toolCallId,
          batchId: payload.batchId,
          message: `No portal handler registered for ${payload.toolName}`,
        }),
      }),
    );

    return;
  }

  try {
    coordinator.notifyHandlerStart({
      batchId: payload.batchId,
      chatId: payload.sessionId,
      toolCallId: payload.toolCallId,
    });
    const handlerResult = await handler({
      input: payload.input,
      toolCallId: payload.toolCallId,
      batchId: payload.batchId,
      applicationId: payload.applicationId,
      sessionId: payload.sessionId,
    });

    await coordinator.completeRequest(
      buildPortalExternalToolResult({
        batchId: payload.batchId,
        chatId: payload.sessionId,
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        handlerResult,
      }),
    );
  } catch (error) {
    console.warn('[PortalToolProvider] Handler execution failed:', error);
    await coordinator.completeRequest(
      buildPortalExternalToolResult({
        batchId: payload.batchId,
        chatId: payload.sessionId,
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        handlerResult: buildPortalToolErrorResult({
          toolName: payload.toolName,
          toolCallId: payload.toolCallId,
          batchId: payload.batchId,
          message:
            error instanceof Error
              ? error.message
              : 'Portal tool execution failed',
        }),
      }),
    );
  } finally {
    coordinator.notifyHandlerEnd({
      batchId: payload.batchId,
      chatId: payload.sessionId,
      toolCallId: payload.toolCallId,
    });
  }
}

export async function handlePortalToolExecute({
  payload,
  coordinator,
  tracker,
  queue,
  isSessionSuppressed = () => false,
}: {
  payload: unknown;
  coordinator: ExternalToolResultCoordinator;
  tracker: PortalExecuteTracker;
  queue: TerminalExecutionQueue;
  isSessionSuppressed?: (sessionId: string) => boolean;
}): Promise<void> {
  const parsed = PortalToolExecutePayloadSchema.safeParse(payload);

  if (!parsed.success) {
    console.warn('[PortalToolProvider] Invalid portal tool SSE payload', {
      error: parsed.error.message,
    });

    return;
  }

  const executePayload = parsed.data;

  if (!hasActiveChat(executePayload.sessionId)) {
    console.info(
      '[PortalToolProvider] Skipping portal tool execute; chat is not active in this window',
      {
        chatId: executePayload.sessionId,
        toolCallId: executePayload.toolCallId,
      },
    );

    return;
  }

  const dropIfForegroundStopped = () =>
    dropSuppressedForegroundExecute({
      coordinator,
      batchId: executePayload.batchId,
      toolCallId: executePayload.toolCallId,
      toolName: executePayload.toolName,
      abortResult: buildHandshakeFailureResult({
        payload: executePayload,
        failureKind: 'abort',
      }).result,
      sessionId: executePayload.sessionId,
      isSessionSuppressed,
    });

  if (dropIfForegroundStopped()) {
    return;
  }

  coordinator.markExecutePending({
    batchId: executePayload.batchId,
    chatId: executePayload.sessionId,
    toolCallId: executePayload.toolCallId,
  });

  await coordinator.awaitPriorFlush(executePayload.sessionId);

  if (dropIfForegroundStopped()) {
    return;
  }

  coordinator.markExecutePending({
    batchId: executePayload.batchId,
    chatId: executePayload.sessionId,
    toolCallId: executePayload.toolCallId,
  });

  const declaration = await coordinator.awaitBatchDeclaration(
    executePayload.batchId,
  );

  if (dropIfForegroundStopped()) {
    return;
  }

  const declarationFailure = handshakeFailureKind(declaration);

  if (declarationFailure) {
    const registration = tracker.registerExecution({
      toolCallId: executePayload.toolCallId,
      chatId: executePayload.sessionId,
    });

    if (registration === 'duplicate') {
      await reflushDuplicateResult({
        coordinator,
        payload: executePayload,
      });

      return;
    }

    await completeHandshakeFailure({
      coordinator,
      payload: executePayload,
      failureKind: declarationFailure,
    });

    return;
  }

  const expected = await coordinator.awaitToolCallExpected({
    batchId: executePayload.batchId,
    toolCallId: executePayload.toolCallId,
  });

  if (dropIfForegroundStopped()) {
    return;
  }

  const expectedFailure = handshakeFailureKind(expected);

  if (expectedFailure) {
    const registration = tracker.registerExecution({
      toolCallId: executePayload.toolCallId,
      chatId: executePayload.sessionId,
    });

    if (registration === 'duplicate') {
      await reflushDuplicateResult({
        coordinator,
        payload: executePayload,
      });

      return;
    }

    await completeHandshakeFailure({
      coordinator,
      payload: executePayload,
      failureKind: expectedFailure,
    });

    return;
  }

  const registration = tracker.registerExecution({
    toolCallId: executePayload.toolCallId,
    chatId: executePayload.sessionId,
  });

  if (registration === 'duplicate') {
    await reflushDuplicateResult({
      coordinator,
      payload: executePayload,
    });

    return;
  }

  coordinator.registerRequest({
    batchId: executePayload.batchId,
    chatId: executePayload.sessionId,
    toolCallId: executePayload.toolCallId,
    toolName: executePayload.toolName,
  });

  await queue.enqueue(executePayload.sessionId, async () => {
    if (dropIfForegroundStopped()) {
      return;
    }

    await executeRegisteredTool({
      coordinator,
      payload: executePayload,
    });
  });
}
